import {
  formatDisplayGrade,
  GRADEBOOK_SETTINGS,
} from '@macmillan-learning/gradebook-shared';
import classNames from 'classnames';
import _ from 'lodash';
import PropTypes from 'prop-types';
import React from 'react';
import { connect } from 'react-redux';

import { selectGradingType } from '../../../../selectors/settings';
import { formatDisplayPercentage } from '../../../../utils/InstructorGradebookHelpers';
import { ISG_REPORT_VIEW_OPTIONS } from '../../../../utils/constants';
import UngradedIcon from '../../../UngradedIcon';
import s from './PointOrGrade.module.css';
import {
  getDisplayPoints,
  renderOutOfPointsLabel,
  titleHelper,
} from './PointOrGradeHelpers';

/* 
  TODO Assignment already references cellData so we should be mapping all cellData attributes from assignment.
  This will simply GridCellDataHelpers
*/
const PointOrGrade = ({
  assignment,
  points,
  outOfTotal,
  grade,
  showPercent,
  showOutOf,
  showPointsDecimals,
  settings,
  isAssigned,
  missing,
  blank,
  showUngrade,
  isTotal,
  weightedCategoryTotal,
  gradingType,
  roundPercentType,
  isgCurrentView,
  displayClass,
}) => {
  const hasValue = !blank && !missing && !showUngrade;

  // Either the regular gradebook (always segmented by categories)
  // or the individual student gradebook category view
  const isCategoryRelated =
    !isgCurrentView || isgCurrentView === ISG_REPORT_VIEW_OPTIONS.CATEGORY;

  const isNonCategoryRelated =
    isgCurrentView && isgCurrentView !== ISG_REPORT_VIEW_OPTIONS.CATEGORY;

  const isWeightedGradingType =
    gradingType === GRADEBOOK_SETTINGS.gradingTypeWeighted;

  const renderBlank = () => {
    return blank ? '' : null;
  };

  const renderMissing = () => {
    return missing ? '-' : null;
  };

  const renderUngradedIcon = () => {
    return showUngrade ? (
      <UngradedIcon
        studentGrade={assignment.studentGrade}
        status={assignment.status}
        tool={assignment.tool}
      />
    ) : null;
  };

  const renderAssignmentDroppedLabel = () => {
    return assignment && assignment.isDropped ? (
      <i className={s.isDropped}>Dropped</i>
    ) : null;
  };

  const renderWeightedPercentTotal = () => {
    // Only categories can have weighted totals
    return hasValue &&
      showPercent &&
      isTotal &&
      isCategoryRelated &&
      isWeightedGradingType
      ? formatDisplayPercentage(weightedCategoryTotal, roundPercentType)
      : null;
  };

  const renderStandardPercentTotal = () => {
    // Category totals when weights are off,
    // or week totals which cannot be weighted, even with weights on
    return hasValue &&
      showPercent &&
      isTotal &&
      ((isCategoryRelated && !isWeightedGradingType) || isNonCategoryRelated)
      ? formatDisplayGrade(
          grade,
          assignment,
          settings,
          isAssigned,
          roundPercentType,
        )
      : null;
  };

  const renderNonTotalPercent = () => {
    return hasValue && showPercent && !isTotal
      ? formatDisplayGrade(
          grade,
          assignment,
          settings,
          isAssigned,
          roundPercentType,
        )
      : null;
  };

  const renderPoints = () => {
    return hasValue && !showPercent ? (
      <div>
        <div
          className={classNames(s.ending, {
            [s.bold]: showOutOf || isTotal,
          })}
        >
          {getDisplayPoints({
            assignment,
            settings,
            isAssigned,
            points,
            outOfTotal,
            isgCurrentView,
          })}
        </div>
        {renderOutOfPointsLabel(outOfTotal, showOutOf, showPointsDecimals)}
      </div>
    ) : null;
  };

  return (
    <div
      title={titleHelper(assignment, settings)}
      className={classNames(displayClass, {
        [s.pointOrPercent]: !displayClass,
        [s.isDropped]: assignment && assignment.isDropped,
        [s.individualStudentGradebook]: !!isgCurrentView,
      })}
    >
      <div
        className={classNames(displayClass, { [s.displayText]: !displayClass })}
      >
        {renderBlank()}
        {renderMissing()}
        {renderUngradedIcon()}
        {renderWeightedPercentTotal()}
        {renderStandardPercentTotal()}
        {renderNonTotalPercent()}
        {renderPoints()}
      </div>
      {renderAssignmentDroppedLabel()}
    </div>
  );
};

PointOrGrade.propTypes = {
  points: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  grade: PropTypes.number,
  outOfTotal: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  showPercent: PropTypes.bool,
  roundPercentType: PropTypes.number,
  isAssigned: PropTypes.bool,
  // eslint-disable-next-line react/no-unused-prop-types
  containerClassName: PropTypes.string,
  showOutOf: PropTypes.bool,
  showPointsDecimals: PropTypes.bool,
  assignment: PropTypes.object,
  settings: PropTypes.array,
  gradingType: PropTypes.number,
  missing: PropTypes.bool,
  blank: PropTypes.bool,
  showUngrade: PropTypes.bool,
  isTotal: PropTypes.bool,
  isgCurrentView: PropTypes.string,
  weightedCategoryTotal: PropTypes.number,
  displayClass: PropTypes.string,
};

function mapStateToProps(state) {
  return {
    gradingType: selectGradingType(state),
    settings: _.get(state, 'courses.course.gradebook.settings', []),
  };
}

export default connect(mapStateToProps)(PointOrGrade);
